class BluetoothService {
  constructor() {
    this.device = null;
    this.server = null;
    this.rxCharacteristic = null;
    this.txCharacteristic = null;
    this.isWebBluetoothSupported = this.checkWebBluetoothSupport();
    this.SERVICE_UUID = '12345678-1234-5678-1234-56789abcdef0';
    this.TX_UUID = '12345678-1234-5678-1234-56789abcdef1';
    this.RX_UUID = '12345678-1234-5678-1234-56789abcdef2';
  }

  checkWebBluetoothSupport() {
    return !!navigator.bluetooth;
  }

  async requestDevice() {
    if (!this.isWebBluetoothSupported) {
      throw new Error('Web Bluetooth is not supported in this browser');
    }

    try {
      this.device = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'SmartLora' }],
        optionalServices: [this.SERVICE_UUID],
      });

      return this.device;
    } catch (error) {
      console.error('Bluetooth device request failed:', error);
      throw error;
    }
  }

  async connectToDevice() {
    if (!this.device) {
      throw new Error('No device selected');
    }

    const MAX_RETRIES = 3;
    let retries = 0;

    while (retries < MAX_RETRIES) {
      try {
        console.log(`Attempting to connect... (Attempt ${retries + 1})`);

        // Add a small delay before attempting to connect
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (!this.device.gatt.connected) {
          this.server = await this.device.gatt.connect();
        }

        const service = await this.server.getPrimaryService(this.SERVICE_UUID);

        this.rxCharacteristic = await service.getCharacteristic(this.RX_UUID);
        this.txCharacteristic = await service.getCharacteristic(this.TX_UUID);

        // Set up disconnection listener
        this.device.addEventListener('gattserverdisconnected', this.onDisconnected.bind(this));

        console.log('Connected to GATT server');
        return this.server;
      } catch (error) {
        console.error(`Connection attempt ${retries + 1} failed:`, error);
        retries++;
        if (retries >= MAX_RETRIES) {
          console.error('Max retries reached. Could not connect to the device.');
          throw error;
        }
        // Wait before the next attempt
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
  }

  onDisconnected(event) {
    console.log('Device disconnected', event);
    // Add reconnection logic here if needed
  }

  async sendWiFiCredentials(ssid, password) {
    if (!this.rxCharacteristic) {
      throw new Error('Device not connected');
    }

    try {
      const data = `${ssid},${password}`;
      const encoder = new TextEncoder();

      console.log('Sending Wi-Fi credentials:', data);

      // BLE has a maximum payload size, so we should handle potential splitting
      const encodedData = encoder.encode(data);
      const chunkSize = 20; // BLE typical max packet size is 20 bytes
      for (let i = 0; i < encodedData.length; i += chunkSize) {
        const chunk = encodedData.slice(i, i + chunkSize);
        await this.rxCharacteristic.writeValue(chunk);
        console.log(`Sent chunk: ${new TextDecoder().decode(chunk)}`);
      }

      // Signal the end of transmission
      await this.rxCharacteristic.writeValue(encoder.encode('EOT'));
      console.log('Wi-Fi credentials sent successfully');
    } catch (error) {
      console.error('Error sending Wi-Fi credentials:', error);
      throw error;
    }
  }

  async startNotifications(callback) {
    if (!this.txCharacteristic) {
      throw new Error('Device not connected');
    }

    try {
      await this.txCharacteristic.startNotifications();
      this.txCharacteristic.addEventListener('characteristicvaluechanged', (event) => {
        const value = new TextDecoder().decode(event.target.value);
        console.log('Notification received:', value);
        callback(value);
      });
      console.log('Notifications started');
    } catch (error) {
      console.error('Error starting notifications:', error);
      throw error;
    }
  }

  async disconnect() {
    if (this.device && this.device.gatt.connected) {
      await this.device.gatt.disconnect();
      console.log('Device disconnected');
    }
  }
}

export default new BluetoothService();
